// Liquid-glass lens maps, after the kube.io method (convex squircle bezel + Snell refraction).
// Pure functions: no DOM, so they can be tested and reused by the TMS.

const squircle = (t) => Math.pow(1 - Math.pow(1 - t, 4), 0.25);

// Sideways shift (px) of a vertical ray at N points across the bezel, outer rim first.
export function refractionProfile(bezel, thickness, ior = 1.5, samples = 128) {
  const eta = 1 / ior, out = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const t = i / samples, h = squircle(t), slope = (squircle(Math.min(1, t + 1e-4)) - h) / 1e-4;
    const len = Math.hypot(slope, 1), nx = -slope / len, ny = -1 / len;
    const k = 1 - eta * eta * (1 - ny * ny);
    if (k < 0) continue;
    const a = eta * ny + Math.sqrt(k), rx = -a * nx, ry = eta - a * ny;
    out[i] = Math.abs(rx * ((h * bezel + thickness) / ry));
  }
  return out;
}

// RGBA pixels for the displacement map (R = x, G = y, 128 = no shift) and the specular rim
// of a W x H rounded rectangle. Only the bezel band is computed; the middle stays flat.
export function lensPixels(W, H, radius, { bezel = 16, thickness = 12, ior = 1.5, light = -Math.PI / 3 } = {}) {
  const R = Math.min(Math.max(radius, bezel), W / 2, H / 2), band = Math.min(bezel, R);
  const profile = refractionProfile(band, thickness, ior), N = profile.length;
  let maxShift = 0;
  for (let i = 0; i < N; i++) maxShift = Math.max(maxShift, profile[i]);
  const map = new Uint8ClampedArray(W * H * 4).fill(128), spec = new Uint8ClampedArray(W * H * 4);
  for (let i = 3; i < map.length; i += 4) map[i] = 255;
  const Lx = Math.cos(light), Ly = Math.sin(light);
  for (let y = 0; y < H; y++) {
    const py = y + 0.5, cy = py < R ? py - R : py > H - R ? py - (H - R) : 0;
    for (let x = 0; x < W; x++) {
      const px = x + 0.5, cx = px < R ? px - R : px > W - R ? px - (W - R) : 0;
      // Straight runs measure from the nearest edge; corners from the corner circle.
      const dist = cx || cy ? Math.hypot(cx, cy) : 0;
      if (!dist) continue;
      const fromRim = R - dist;
      if (fromRim < 0 || fromRim >= band) continue;
      const nx = cx / dist, ny = cy / dist, o = (y * W + x) * 4;
      const m = maxShift ? profile[Math.min(N - 1, ((fromRim / band) * N) | 0)] / maxShift : 0;
      // A convex lens samples from further inside: shift against the outward normal.
      map[o] = 128 - 127 * nx * m;
      map[o + 1] = 128 - 127 * ny * m;
      if (fromRim < 3) {
        const a = Math.abs(nx * Lx + ny * Ly), fade = Math.sin(Math.PI * Math.min(1, (fromRim + 0.5) / 3));
        spec[o] = spec[o + 1] = spec[o + 2] = 255;
        spec[o + 3] = 255 * (0.25 + 0.75 * a * a) * fade;
      }
    }
  }
  return { map, spec, maxShift };
}

// Presets from the research: capsule controls, bars/search fields, floating cards.
export const GLASS_PRESETS = {
  capsule: { bezel: 14, thickness: 10, strength: 0.9, blur: 1, saturate: 1.6, rim: 4, specular: 0.5 },
  bar: { bezel: 18, thickness: 14, strength: 0.85, blur: 2, saturate: 1.7, rim: 5, specular: 0.45 },
  card: { bezel: 24, thickness: 18, strength: 0.65, blur: 10, saturate: 1.7, rim: 4, specular: 0.4 },
};

export const glassKey = (W, H, R, name) => `g${W}x${H}r${Math.round(R)}${name}`.replace(/[^a-z0-9]/gi, "");
